// Mount gate + viewer identity for SlotAvailabilityCard.
//
// P2 only resolved a customer viewer (no auth/ownership check at all). P4
// adds `canManage` on top rather than replacing anything - the mount gate
// itself (flag ∧ rental ∧ businessId) doesn't change once owner states
// exist. Collaborators get `canManage: true` too (plan doc §7 Q3 - "show to
// collaborators also"); the API's writes stay owner-effective-only
// regardless, so a collaborator's Publish tap can still 403 and the usual
// friendly-error path handles that same as any other write failure.

import { useProductInfo } from "./useProductInfo";
import { useListingTypeConfig } from "./useListingTypeConfig";
import { useCanManageListing } from "./useCanManageListing";
import AppConfig from "../../../../../config/config";

export interface UseSlotCalendarViewerResult {
  /** False hides SlotAvailabilityCard entirely - not a rental, no business
   *  behind the listing (the API's calendar is always business-scoped), or
   *  the feature flag is off. */
  shouldMount: boolean;
  productId: string;
  businessId: string | undefined;
  /** Owner or business collaborator - shows the Manage pill and the
   *  draft/not-configured owner states (P4). */
  canManage: boolean;
}

/**
 * THE single on/off decision for the whole feature on a listing - the product
 * page, and the Setup/Manage routes, all ask this. False = nothing renders,
 * no calendar code is even downloaded (the card is lazy) and no calendar API
 * is called. True requires: global flag, the business's own switch
 * (business config enableSlotCalendar, carried on the product), a business
 * behind the listing, and - until the backend drops its rental-only rule
 * (14307) - a rental listing.
 */
export function useSlotCalendarGate(): boolean {
  const { productId, businessId, listingType, slotCalendarEnabled } = useProductInfo();
  const { listingTypeId } = useListingTypeConfig(listingType);
  return (
    AppConfig.featureFlags.enableSlotCalendar &&
    slotCalendarEnabled &&
    listingTypeId === "rental" &&
    Boolean(businessId) &&
    Boolean(productId)
  );
}

export function useSlotCalendarViewer(): UseSlotCalendarViewerResult {
  const { productId, businessId, isCurrentUserSeller } = useProductInfo();
  const canManage = useCanManageListing(isCurrentUserSeller, businessId);
  const shouldMount = useSlotCalendarGate();

  return { shouldMount, productId, businessId, canManage };
}
